import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, poundsFromPence } from "../lib/api";
import { PublicShell } from "./PublicLandingPage";
import { BookingHeader, SummaryPanel, TimeslotPicker } from "../components/BookingChrome";
import { getTemplate, mergeBranding } from "../lib/templates";
import { formatSlotTime } from "../lib/timeFormat";

// Editor live-preview for the Time Slots tab. Renders the slot-selection step with
// sample data and applies branding pushed from the editor via postMessage.
export default function TimeslotPreviewPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [selected, setSelected] = useState(null);
  const [liveBranding, setLiveBranding] = useState(null);
  const gotRef = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const list = await api.get(`/public/attraction/${slug}`);
        const es = (list.data.events || [])[0]?.slug;
        if (!es) { setErr("No events available."); return; }
        const ev = await api.get(`/public/attraction/${slug}/event/${es}`);
        const dates = ev.data.dates || [];
        let picked = null;
        for (const d of dates) {
          const det = await api.get(`/public/attraction/${slug}/date/${d.date_id}`);
          if ((det.data.time_slots || []).length > 0) { picked = det.data; break; }
          if (!picked) picked = det.data; // fall back to first
        }
        setData(picked || { attraction: ev.data.attraction, event: ev.data.event, date: dates[0], time_slots: [] });
        if (!gotRef.current) setLiveBranding((cur) => cur ?? (ev.data.attraction.branding || {}));
      } catch { setErr("Preview unavailable."); }
    })();
  }, [slug]);

  useEffect(() => {
    const handler = (e) => {
      if (e?.data?.type === "BRANDING_UPDATE" && e.data.branding) { gotRef.current = true; setLiveBranding(e.data.branding); }
    };
    window.addEventListener("message", handler);
    try { window.parent?.postMessage({ type: "PREVIEW_READY" }, "*"); } catch (_) {}
    return () => window.removeEventListener("message", handler);
  }, []);

  if (err) return <PublicShell><div className="text-center py-32 text-[#fca5a5]">{err}</div></PublicShell>;
  if (!data) return <PublicShell><div className="text-center py-32 text-[#8a8a8a]">Loading…</div></PublicShell>;

  const a = data.attraction;
  const b = mergeBranding(liveBranding || a.branding);
  const aShell = { ...a, branding: b };
  const tplId = getTemplate(b.template_id || "default").id;
  const PANEL_HEADINGS = { default: "Your Visit", carnival: "Your Booking", haunt: "Your Visit", asylum: "Admission Record", crypt: "Your Visit" };
  const slot = (data.time_slots || []).find((s) => s.id === selected);

  return (
    <PublicShell attraction={aShell} hideLogo>
      <BookingHeader attraction={a} branding={b} />
      <div className="booking-body" data-testid="booking-body">
        <TimeslotPicker slots={data.time_slots} branding={b} attraction={aShell} selectedId={selected} onSelect={(s) => setSelected(s.id)} />
        <SummaryPanel
          heading={b.summary_heading || PANEL_HEADINGS[tplId] || "Your Visit"}
          dateText={data.date?.date || "—"}
          showTime
          timeText={slot ? formatSlotTime(slot.start_time, slot.end_time, aShell) : "—"}
          ticketsText="—"
          totalText="—"
          cta={{ label: b.slot_cta_text || "Continue", disabled: !selected, onClick: () => {} }}
        />
      </div>
    </PublicShell>
  );
}
